import { Component, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
  CtBinaryOperator,
  CtButtonConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelOnFlyCreateService,
  CtModelOperator,
  CtModelRouteData,
  CtModelService,
  CtModelType,
  MAT_RAISED_PRIMARY,
  SnackbarService
} from "@ctsolution/ct-framework";
import { CompanyInformationService } from "./company-information.service";
import { CompanyInformationRouteData } from "../../../_core/route-data/company-information.route-data";
import { CompanyInformation } from "./company-information";
import { CtWebapiGenericResponse, CtWebapiService, DataRequest } from "@ctsolution/ct-webapi";
import { Observer } from 'rxjs';
import { CompanyService } from '../company.service';
import { CustomCompanySetupEnum } from '../company-edit/company-edit.component';
import { CTConfigurationModel } from 'projects/ct-manager/src/app/_core/classes/ct-configuration-model';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { DEFAULT_DATATABLE_CONFIGURATION } from '../../../_core/constants';

@Component({
  selector: 'app-company-information',
  template: `
    <h5> {{ companyInformationConfiguration.label | translate }} </h5>
    <div class="company-information-container" *ngIf="modelConfiguration">
      <mat-card>
        <mat-card-content>
          <div class="ct-model-container">
            <ct-model [configuration]="modelConfiguration"></ct-model>
          </div>
          <mat-card-actions *ngIf="modelConfiguration?.form" align="end">
            <ct-button [configuration]="submitButton"></ct-button>
          </mat-card-actions>
        </mat-card-content>
      </mat-card>
    </div>
    <div style="margin-top:20px" class="ct-model-container" *ngIf="extraModelConfiguration">
      <ct-model [configuration]="extraModelConfiguration"></ct-model>
    </div>
  `,
  styles: [`
    ::ng-deep .company-information-container {
      .ct-model-container {
        .mat-card {
          margin: 0;
          box-shadow: none !important;
          .mat-card-content {
            padding: 0 !important;
          }
        }
      }
    }
  `]
})
export class CompanyInformationComponent implements OnInit {

  @Input() masterConfiguration: CtModelConfiguration<any> | null = null;
  @Input() parentComponent: any; // Riferimento al componente parent CompanyEditComponent

  modelConfiguration: CtModelConfiguration<any> | null = null;
  extraModelConfiguration: CtModelConfiguration<any> | null = null;
  leadOid: string | null = null;

  companyInformationConfiguration: CTConfigurationModel = CTConfigurationModel.create().setLabel("Contatti");

  submitButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("CT_PAGE.CT_FORM.save")
    .setClass('ct-model-submit-button')
    .setAction(() => this.handleSubmit())
    .setMatherialOptions(MAT_RAISED_PRIMARY);

  constructor(
    private onFlyCreateHelper: CtModelOnFlyCreateService,
    private managerService: CtManagerService,
    private companyContactService: CompanyInformationService,
    private companyService: CompanyService,
    private _model: CtModelService<any>,
    private _webapi: CtWebapiService,
    private _snackbar: SnackbarService,
    private route: ActivatedRoute
  ) {}

  async ngOnInit() {
    // Override del componente se definito nel manager
    await this.managerService.getComponentOverride("company", "companyInformationConfiguration", this.companyInformationConfiguration);

    // Verifica che sia disponibile un ID nel master per procedere con il setup
    if (this.masterConfiguration?.RouteData?.id) {
      switch (this.companyInformationConfiguration.customCompanySetup) {
        case CustomCompanySetupEnum.CUSTOM:
          this.customSetup();
          break;
        case CustomCompanySetupEnum.BASE:
          this.setup();
          break;
        case CustomCompanySetupEnum.BOTH:
          this.bothSetup();
          break;
        default:
          this.setup();
          break;
      }
    }
  }

  /**
   * Setup personalizzato (CUSTOM)
   */
  customSetup() {
    this.leadOid = this.route.snapshot.queryParamMap.get('LeadOid');

    const configuration: CtModelConfiguration<any> = CtModelConfiguration.create()
      .setRouteData(
        CtModelRouteData.create()
          .setModelType(CtModelType.FORM)
          .setController(`${window.location.origin}/assets/demo/${this.companyInformationConfiguration.customCompanyTemplate}`)
      )
      .setDefaultActionsEnabled(false)
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setScrollbarH(false));

    this.modelConfiguration = configuration;

    const body: CtModelDatatableOperators = CtModelDatatableOperators.create().setFilters([
      CTModelDatatableFilter.create()
        .setField('Company.Oid')
        .setValue(this.masterConfiguration?.RouteData?.id)
        .setOperatorType(CtBinaryOperator.Equal)
    ]);

    const request: DataRequest = new DataRequest()
      .setController([CompanyInformationRouteData().controller!])
      .setAction('List')
      .setQueryParams({ hideSpinner: true })
      .setBody(body);

    this._webapi.post<CtModelConfiguration<any>>(request).subscribe((result: CtWebapiGenericResponse<CtModelConfiguration<any>>) => {
      this.modelConfiguration?.setDataSource(result.Result.DataSource);
      // Imposta i valori del form dopo aver caricato il datasource
      setTimeout(() => {
        this.setupFormValue(this.modelConfiguration?.DataSource);
      }, 100);
    });
  }

  /**
   * Setup standard (BASE)
   */
  setup() {
    this.leadOid = this.route.snapshot.queryParamMap.get('LeadOid');

    const configuration = CtModelConfiguration.create()
      .setRouteData(CompanyInformationRouteData())
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION())
      .setDisableAutoQueryFilter(true)
      .setOnEdit(event => { this.onStandardEdit(event.Oid); })
      .setOnCreate(() => { this.onStandarCreate(); })
      .setFieldsCustomTemplates([])
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setScrollbarH(false));

    configuration.initialized().subscribe(() => {
      this.modelConfiguration?.setOperations([CtModelOperator.Update, CtModelOperator.Create]);
    });

    configuration.filterValues.addConstantFilter(
      CTModelDatatableFilter.create()
        .setField('Company.Oid')
        .setValue(this.masterConfiguration?.RouteData?.id)
        .setOperatorType(CtBinaryOperator.Equal)
    );

    this.modelConfiguration = configuration;
  }

  /**
   * Setup combinato (BOTH) che unisce CUSTOM e BASE
   */
  bothSetup() {
    this.customSetup();
    this.leadOid = this.route.snapshot.queryParamMap.get('LeadOid');

    const configuration = CtModelConfiguration.create()
      .setRouteData(CompanyInformationRouteData())
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION())
      .setDisableAutoQueryFilter(true)
      .setOnEdit(event => { this.onStandardEdit(event.Oid); })
      .setOnCreate(() => { this.onStandarCreate(); })
      .setFieldsCustomTemplates([])
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setScrollbarH(false));

    configuration.initialized().subscribe(() => {
      this.modelConfiguration?.setOperations([CtModelOperator.Update, CtModelOperator.Create]);
    });

    configuration.filterValues.addConstantFilter(
      CTModelDatatableFilter.create()
        .setField('Company.Oid')
        .setValue(this.masterConfiguration?.RouteData?.id)
        .setOperatorType(CtBinaryOperator.Equal)
    );

    this.extraModelConfiguration = configuration;
  }

  /**
   * Imposta i valori iniziali del form in base ai dati esistenti
   * @param value Array di CompanyInformation
   */
  setupFormValue(value: Array<CompanyInformation>) {
    const formValue = this.modelConfiguration?.form?.value;
    this.companyContactService.getContactInformationTypes().subscribe(companyInformationTypes => {
      Object.keys(formValue).forEach(k => {
        const codeValue = companyInformationTypes.find(element => element.Description?.toLowerCase() === k.toLowerCase());
        const existingValue = value.find(element => +(element.Type ?? '0') === +(codeValue?.Code ?? '0'));
        if (existingValue) {
          this.modelConfiguration?.form?.get(k)?.setValue(existingValue.Content);
        }
      });
    });
  }

  /**
   * Apre il form per la modifica standard
   * @param Oid Identificativo dell'informazione da modificare
   */
  onStandardEdit(Oid: number | null = null) {
    const routeData: CtModelRouteData = CtModelRouteData.create()
      .setController(CompanyInformationRouteData().controller)
      .setModelType(CtModelType.FORM);

    const configuration: CtModelConfiguration<any> = CtModelConfiguration.create();
    if (Oid) {
      routeData.setId(Oid);
    }
    configuration.setRouteData(routeData).setFieldsCustomTemplates([{}]);

    this.onFlyCreateHelper.openOnFlyCreate(configuration)
      .afterClosed()
      .subscribe(reload => {
        if (reload) {
          // Refresh forzato, in attesa di una corretta navigazione tramite router
          window.location.reload();
        }
      });
  }

  /**
   * Apre il form per la creazione standard
   */
  onStandarCreate() {
    const routeData: CtModelRouteData = CtModelRouteData.create()
      .setController(CompanyInformationRouteData().controller)
      .setModelType(CtModelType.FORM);

    const configuration: CtModelConfiguration<any> = CtModelConfiguration.create();
    configuration.setRouteData(routeData);

    if (this.masterConfiguration?.RouteData?.id) {
      configuration.filterValues.addConstantFilter(
        CTModelDatatableFilter.create()
          .setField('CompanyOid')
          .setValue(this.masterConfiguration?.RouteData?.id)
          .setOperatorType(CtBinaryOperator.Equal)
      );
    }

    this.onFlyCreateHelper.openOnFlyCreate(configuration)
      .afterClosed()
      .subscribe(reload => {
        if (this.leadOid) {
          this.companyService.navigateToLead(this.leadOid);
        } else if (reload) {
          window.location.reload();
        }
      });
  }

  /**
   * Gestisce il click sul pulsante "Salva"
   * Se siamo in modalità edit (con parentComponent disponibile), chiama il submit sincronizzato
   * Altrimenti, esegue il submit tradizionale standalone
   */
  handleSubmit(): void {
    // Controlla se siamo in modalità edit verificando se esiste un id nel masterConfiguration
    const isEditMode = this.masterConfiguration?.RouteData?.id;
    
    // Se abbiamo il parent component, siamo in edit mode E il metodo submitAll esiste, usa il submit sincronizzato
    if (this.parentComponent && this.parentComponent.submitAll && isEditMode) {
      this.parentComponent.submitAll();
    } else {
      // Altrimenti usa il metodo tradizionale standalone
      this.onSubmit();
    }
  }

  /**
   * Metodo esposto per il submit sincronizzato dal parent component
   * Restituisce un Observable per essere usato con forkJoin
   * @returns Observable<any>
   */
  submitForm(): Observable<any> {
    return new Observable(observer => {
      
      // Se non c'è un form da salvare, completa immediatamente con successo
      if (!this.modelConfiguration?.form) {
        observer.next({ success: true, message: 'No form to save' });
        observer.complete();
        return;
      }

      // Validazione già fatta dal parent, procedo direttamente con il salvataggio
      const value = this.modelConfiguration.form.value;
      const keys = Object.keys(value);

      // Se non ci sono campi, completa con successo
      if (keys.length === 0) {
        observer.next({ success: true, message: 'No fields to save' });
        observer.complete();
        return;
      }

      this.companyContactService.getContactInformationTypes().subscribe({
        next: (companyInformationTypes) => {
          
          let completedRequests = 0;
          const totalRequests = keys.length;
          let hasError = false;

          keys.forEach((k) => {
            const codeValue = companyInformationTypes.find(element => element.Description?.toLowerCase() === k.toLowerCase());
            
            if (codeValue) {
              const companyInformation = CompanyInformation.create()
                .setCompanyOid(this.masterConfiguration?.RouteData?.id ?? -1)
                .setContent(value[k] ?? '')
                .setType(codeValue.Code);

              const configuration: CtModelConfiguration<any> = CtModelConfiguration.create()
                .setRouteData(
                  CtModelRouteData.create()
                    .setController(CompanyInformationRouteData().controller)
                );

              const existingValue: CompanyInformation | undefined = this.modelConfiguration?.DataSource.find(
                (element: CompanyInformation) => element.Type != null && (+element.Type) === +(companyInformation.Type ?? '0')
              );

              if (existingValue) {
                configuration.RouteData?.setId(existingValue.Oid ?? -1);
              }

              this._model.putInfos(configuration, companyInformation)?.subscribe({
                next: () => {
                  completedRequests++;
                  console.log(`Company information ${k} salvata (${completedRequests}/${totalRequests})`);
                  
                  // Se tutte le richieste sono completate, completa l'observable
                  if (completedRequests === totalRequests && !hasError) {
                    observer.next({ success: true, message: 'All company information saved' });
                    observer.complete();
                  }
                },
                error: (err) => {
                  hasError = true;
                  console.error(`Errore durante il salvataggio di ${k}:`, err);
                  observer.error(err);
                }
              });
            } else {
              // Se non trova il codeValue, conta comunque come completato
              completedRequests++;
              if (completedRequests === totalRequests && !hasError) {
                observer.next({ success: true, message: 'All company information saved' });
                observer.complete();
              }
            }
          });
        },
        error: (err) => {
          console.error('Errore durante il recupero dei tipi informazione:', err);
          observer.error(err);
        }
      });
    });
  }

  /**
   * Gestisce il salvataggio del form (metodo standalone legacy)
   * Questo metodo viene usato solo quando il componente non è in una configurazione sincronizzata
   */
  onSubmit() {
    this.modelConfiguration?.form?.markAllAsTouched();

    if (this.modelConfiguration?.form?.valid) {
      const value = this.modelConfiguration.form.value;
      const keys = Object.keys(value);

      this.companyContactService.getContactInformationTypes().subscribe(companyInformationTypes => {
        keys.forEach((k, idx) => {
          const codeValue = companyInformationTypes.find(element => element.Description?.toLowerCase() === k.toLowerCase());
          if (codeValue) {
            const companyInformation = CompanyInformation.create()
              .setCompanyOid(this.masterConfiguration?.RouteData?.id ?? -1)
              .setContent(value[k] ?? '')
              .setType(codeValue.Code);

            const configuration: CtModelConfiguration<any> = CtModelConfiguration.create()
              .setRouteData(
                CtModelRouteData.create()
                  .setController(CompanyInformationRouteData().controller)
              );

            const existingValue: CompanyInformation | undefined = this.modelConfiguration?.DataSource.find(
              (element: CompanyInformation) => element.Type != null && (+element.Type) === +(companyInformation.Type ?? '0')
            );

            if (existingValue) {
              configuration.RouteData?.setId(existingValue.Oid ?? -1);
            }

            this._model.putInfos(configuration, companyInformation)?.subscribe(() => {
              if (idx === (keys.length - 1)) {
                this._snackbar.onSuccess();
                this.modelConfiguration = null;
                setTimeout(() => window.location.reload());
                if (this.leadOid) {
                  this.companyService.navigateToLead(this.leadOid);
                }
              }
            });
          }
        });
      });
    }
  }
}
